import { z } from "zod/v4";
import { apiFetch, ApiError } from "@/lib/apiClient";

const accountAccessSchema = z.object({
  id: z.string(),
  role: z.enum(["USER", "ADMIN"]),
  isBanned: z.boolean(),
  isLocked: z.boolean(),
  isPendingDelete: z.boolean(),
  isDeleting: z.boolean(),
});

export type AccountAccess = z.infer<typeof accountAccessSchema>;

export async function getAccountAccess(signal?: AbortSignal) {
  return accountAccessSchema.parse(await apiFetch("/users/me", { signal }));
}

export function accountAccessOptions(userId: string) {
  return {
    queryKey: ["account-access", userId],
    queryFn: ({ signal }: { signal: AbortSignal }) => getAccountAccess(signal),
    retry: false,
    staleTime: 0,
  };
}

export function canReadAdminUsers(
  account: AccountAccess | undefined,
  actorId: string,
  pathname: string,
) {
  return Boolean(
    account &&
    account.id === actorId &&
    account.role === "ADMIN" &&
    !account.isBanned &&
    !account.isLocked &&
    !account.isDeleting &&
    (/^\/admin\/users(?:\/[^/]+(?:\/edit)?)?\/?$/.test(pathname) ||
      [
        "/admin/messages",
        "/admin/general",
        "/admin/attributions",
        "/admin/authentication",
        "/admin/legal",
        "/admin/app-store-links",
        "/admin/queues",
        "/admin/jobs",
        "/admin/plans",
      ].includes(pathname) ||
      /^\/admin\/email-templates(?:\/[^/]+)?\/?$/.test(pathname)),
  );
}
export function accountAccessDestination(
  account: AccountAccess | undefined,
  error: unknown,
  adminRead?: { actorId: string; pathname: string },
) {
  if (
    account?.isBanned ||
    (error instanceof ApiError && error.code === "ACCOUNT_BANNED")
  )
    return "/banned";
  if (
    account?.isLocked ||
    (error instanceof ApiError && error.code === "ACCOUNT_LOCKED")
  )
    return "/locked";
  if (account?.isDeleting) return "/auth/sign-in";
  if (
    account?.isPendingDelete &&
    !error &&
    adminRead &&
    canReadAdminUsers(account, adminRead.actorId, adminRead.pathname)
  )
    return null;
  if (
    account?.isPendingDelete ||
    (error instanceof ApiError && error.code === "ACCOUNT_PENDING_DELETION")
  )
    return "/pending-deletion";
  if (error instanceof ApiError && error.status === 401) return "/auth/sign-in";
  return null;
}
